import * as React from 'react';
import { styled } from '@linaria/react';

import { clamp, normalize } from '@/utils';
import useBoundingBox from '@/hooks/use-bounding-box';

import VisuallyHidden from '@/components/VisuallyHidden';
import CoordDisplay from './CoordDisplay';

interface Point {
  angle: number;
  distance: number;
}

interface Props {
  size: number;
}

function calculateAngleLabelTransform(angle: number) {
  const radiusX = 50;
  const radiusY = radiusX - 5;
  const smoothedAngle =
    angle < 90
      ? normalize(angle, 0, 90, -10, 90 * 0.5)
      : normalize(angle, 90, 360, 90 * 0.5, 270);
  const angleInRadians = (smoothedAngle * Math.PI) / 180;

  const x = radiusX * Math.cos(angleInRadians);
  const y = radiusY * Math.sin(angleInRadians);

  return `translate(-50%, -50%) translate(${x - 2}px, ${y}px)`;
}

function getDistanceLabelTransform(point: Point) {
  const verticalOffset = point.angle < 270 ? 10 : -10;
  const isFlipped = point.angle > 90 && point.angle < 270;

  return `
    translate(-50%, -50%)
    rotate(${point.angle}deg)
    translate(${point.distance / 2}px, ${verticalOffset}px)
    rotate(${isFlipped ? 180 : 0}deg)
  `;
}

function createArcPath(angle: number, fill: boolean = false) {
  const radius = 25;
  const centerX = 25;
  const centerY = 25;

  const angleInRadians = (angle * Math.PI) / 180;

  const endX = centerX + radius * Math.cos(angleInRadians);
  const endY = centerY + radius * Math.sin(angleInRadians);

  const largeArcFlag = angle > 180 ? 1 : 0;

  if (fill) {
    return `
      M ${centerX} ${centerY}
      L ${centerX + radius} ${centerY}
      A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}
      Z
    `;
  } else {
    return `
      M ${centerX + radius} ${centerY}
      A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}
    `;
  }
}

function PolarGraph({ size }: Props) {
  const [point, setPoint] = React.useState<Point>({
    angle: 110,
    distance: 125,
  });
  const pointRef = React.useRef<Point>(point);
  pointRef.current = point;

  const [isDragging, setIsDragging] = React.useState(false);
  const graphRef = React.useRef<HTMLDivElement>(null);

  const graphBB = useBoundingBox(graphRef, 100);

  const updatePointPosition = React.useCallback(
    (event: PointerEvent | React.PointerEvent) => {
      if (!graphBB) {
        return;
      }

      const radius = size / 2;

      const centerX = graphBB.left + radius;
      const centerY = graphBB.top + radius;
      const x = event.clientX - centerX;
      const y = event.clientY - centerY;

      let angle = Math.atan2(y, x) * (180 / Math.PI);

      if (angle < 0) {
        angle += 360;
      }

      if (Math.round(angle) === 360) {
        angle = 0;
      }

      // Calculate distance (0 to radius)
      const distance = Math.min(Math.sqrt(x * x + y * y), radius);

      setPoint({
        angle: Math.round(angle),
        distance: Math.round(distance),
      });
    },
    [graphBB, size]
  );

  React.useEffect(() => {
    if (!isDragging) {
      return;
    }

    function handlePointerMove(event: PointerEvent) {
      event.preventDefault();
      updatePointPosition(event);
    }

    function handlePointerUp() {
      setIsDragging(false);
    }

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging, updatePointPosition]);

  const handlePointerDown = (event: React.PointerEvent) => {
    event.preventDefault();
    setIsDragging(true);
    updatePointPosition(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setPoint({
        angle: point.angle,
        distance: Math.max(point.distance - 5, 0),
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setPoint({
        angle: point.angle,
        distance: Math.min(point.distance + 5, size / 2),
      });
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setPoint({
        angle: Math.max(point.angle - 5, 0),
        distance: point.distance,
      });
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      setPoint({
        angle: Math.min(point.angle + 5, 360),
        distance: point.distance,
      });
    }
  };

  // Convert polar coordinates back to cartesian for display
  const angleInRadians = (point.angle * Math.PI) / 180;
  const x = point.distance * Math.cos(angleInRadians);
  const y = point.distance * Math.sin(angleInRadians);

  const showArc = point.distance > 25;
  const showAngleLabel = showArc && point.angle > 20;
  const showDistanceLabel = point.distance > 25;

  const angleFillPath = createArcPath(point.angle, true);
  const angleStrokePath = createArcPath(point.angle, false);

  return (
    <Wrapper>
      <Graph
        ref={graphRef}
        onPointerDown={handlePointerDown}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
        }}
      >
        <GridLines />

        <Arc
          viewBox="0 0 50 50"
          width={50}
          height={50}
          xmlns="http://www.w3.org/2000/svg"
          style={{ opacity: showArc ? 1 : 0 }}
        >
          <path
            d={angleFillPath}
            fill="hsl(var(--main-color-fragment) / 0.2)"
          />
          <path
            d={angleStrokePath}
            stroke="hsl(var(--main-color-fragment) / 1)"
            strokeWidth={2}
            fill="none"
          />
        </Arc>
        <DistanceLine
          style={{
            width: point.distance,
            transform: `rotate(${point.angle}deg)`,
          }}
        />

        <AngleLabel
          style={{
            opacity: showAngleLabel ? 1 : 0,
            transition: point.angle < 10 ? 'none' : 'opacity 250ms',
            transform: calculateAngleLabelTransform(point.angle),
          }}
        >
          {point.angle}°
        </AngleLabel>

        <DistanceLabel
          style={{
            opacity: showDistanceLabel ? 1 : 0,
            transform: getDistanceLabelTransform(point),
          }}
        >
          {point.distance}
        </DistanceLabel>

        <Point
          onKeyDown={handleKeyDown}
          style={{
            transform: `translate(${x}px, ${y}px)`,
          }}
        >
          <VisuallyHidden>
            Distance: {point.distance}px, Angle: {point.angle}°
          </VisuallyHidden>
        </Point>
      </Graph>
      <CoordDisplay
        values={[
          {
            value: point.angle + '°',
            label: 'Angle',
          },
          {
            value: point.distance + 'px',
            label: 'Distance',
          },
        ]}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Graph = styled.div`
  position: relative;
  outline: 2px solid var(--color-gray-200);
  outline-offset: -1px;
  cursor: pointer;
  background: repeating-radial-gradient(
    circle at center,
    transparent,
    transparent 9px,
    var(--color-gray-200) 9px,
    var(--color-gray-200) 10px
  );
  touch-action: none;
  overflow: hidden;
`;

const GridLines = styled.div`
  position: absolute;
  inset: 0;

  &::before,
  &::after {
    content: '';
    position: absolute;
    background: var(--color-gray-200);
  }

  &::before {
    top: 50%;
    left: 0;
    right: 0;
    height: 2px;
    transform: translateY(-50%);
  }

  &::after {
    top: 0;
    left: 50%;
    bottom: 0;
    width: 2px;
    transform: translateX(-50%);
  }
`;

const Point = styled.button`
  --size: 16px;

  position: absolute;
  top: 50%;
  left: 50%;
  width: var(--size);
  height: var(--size);
  background: var(--main-color);
  border: 2px solid var(--color-background);
  border-radius: 50%;
  transform-origin: center;
  margin: calc(var(--size) / -2) 0 0 calc(var(--size) / -2);

  &:focus-visible {
    outline: 2px solid var(--main-color);
    outline-offset: 0px;
    background: var(--color-text);
  }

  [data-color-mode='dark'] &:focus-visible {
    background: white;
  }
`;

const Arc = styled.svg`
  --size: 50px;

  position: absolute;
  top: calc(50% - var(--size) / 2);
  left: calc(50% - var(--size) / 2);
  width: var(--size);
  height: var(--size);
  overflow: visible;
  transition: opacity 250ms;
`;

const DistanceLine = styled.div`
  position: absolute;
  top: calc(50% - 1px);
  left: 50%;
  height: 2px;
  background: var(--color-text);
  transform-origin: left center;
`;

const Label = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  font-size: 0.75rem;
  color: var(--color-text);
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
  transition: opacity 250ms;
`;

const AngleLabel = styled(Label)`
  transform-origin: center center;
`;

const DistanceLabel = styled(Label)`
  transform-origin: center center;
`;

export default PolarGraph;
